import React, { useState, useEffect } from 'react';
import { X, Save, User, Tag, AlertTriangle } from 'lucide-react';

interface Personne {
  id: string;
  nom: string;
  email: string;
  telephone: string;
  adresse: string;
  description: string;
  statut: 'actif' | 'inactif' | 'suspect';
  tags: string[];
}

interface PersonneModalProps {
  isOpen: boolean;
  onClose: () => void;
  personne?: Personne | null;
  onSave: (personne: Omit<Personne, 'id'>) => Promise<void>;
  mode: 'add' | 'edit';
}

const PersonneModal: React.FC<PersonneModalProps> = ({ 
  isOpen, 
  onClose, 
  personne, 
  onSave, 
  mode 
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    description: '',
    statut: 'actif' as 'actif' | 'inactif' | 'suspect',
    tags: [] as string[]
  });
  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (personne && mode === 'edit') {
      setFormData({
        nom: personne.nom,
        email: personne.email,
        telephone: personne.telephone,
        adresse: personne.adresse,
        description: personne.description,
        statut: personne.statut,
        tags: [...personne.tags]
      });
    } else {
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        adresse: '',
        description: '',
        statut: 'actif',
        tags: []
      });
    }
    setErrors({});
  }, [personne, mode, isOpen]);

  // Gestion de la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (formData.telephone && !/^[\d\s+\-()]+$/.test(formData.telephone)) {
      newErrors.telephone = 'Numéro de téléphone invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        await onSave(formData);
        onClose();
      } catch (error) {
        console.error('Error saving person:', error);
        // Keep modal open if there's an error
      }
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 w-screen h-screen flex items-center justify-center z-[99999] p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
      <div 
        className="w-full max-w-4xl bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700/50 transform transition-all duration-300 ease-out animate-slideUp" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header moderne */}
        <div className="relative p-8 bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-b border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-1">
                  {mode === 'add' ? 'Ajouter une personne' : 'Modifier la personne'}
                </h2>
                <p className="text-gray-400">Gestion des personnes dans l'enquête</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-12 h-12 rounded-2xl bg-gray-700/50 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all duration-300 flex items-center justify-center backdrop-blur-sm"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nom */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom complet *</label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                  errors.nom 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50'
                } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                placeholder="Nom et prénom"
                required
              />
              {errors.nom && (
                <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{errors.nom}</span>
                </div>
              )}
            </div>

            {/* Email */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                  errors.email 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50'
                } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                placeholder="email@exemple.com"
              />
              {errors.email && (
                <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{errors.email}</span>
                </div>
              )}
            </div>

            {/* Téléphone */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Téléphone</label>
              <input
                type="tel"
                value={formData.telephone}
                onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                  errors.telephone 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50'
                } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                placeholder="06 12 34 56 78"
              />
              {errors.telephone && (
                <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{errors.telephone}</span>
                </div>
              )}
            </div>

            {/* Statut */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Statut</label>
              <select
                value={formData.statut}
                onChange={(e) => setFormData(prev => ({ ...prev, statut: e.target.value as any }))}
                className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
              >
                <option value="actif">Actif</option>
                <option value="inactif">Inactif</option>
                <option value="suspect">Suspect</option>
              </select>
            </div>
          </div>

          {/* Adresse */}
          <div className="relative group">
            <label className="block text-sm font-medium text-gray-300 mb-2">Adresse</label>
            <input
              type="text"
              value={formData.adresse}
              onChange={(e) => setFormData(prev => ({ ...prev, adresse: e.target.value }))}
              className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
              placeholder="123 Rue de la Paix, 75001 Paris"
            />
          </div>

          {/* Description */}
          <div className="relative group">
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300 resize-none"
              placeholder="Description de la personne, son rôle dans l'enquête..."
              rows={4}
            />
          </div>

          {/* Tags */}
          <div className="relative group">
            <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
            <div className="flex flex-wrap gap-3 mb-4">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500/20 to-orange-600/20 text-orange-400 text-sm rounded-2xl flex items-center space-x-2 border border-orange-500/30 backdrop-blur-sm"
                >
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-red-400 transition-colors rounded-full p-1 hover:bg-red-500/20"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-3">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                placeholder="Ajouter un tag"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-2xl text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Tag className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-8 border-t border-gray-700/30">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white rounded-2xl font-medium transition-all duration-300 backdrop-blur-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Save className="w-5 h-5" />
              <span>{mode === 'add' ? 'Ajouter' : 'Modifier'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonneModal; 